import React, { useState } from "react";
import { DateTime } from "luxon";
import { CRMColors } from "../../../models/CRMColors";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { Background } from "../../BuildingBlocks/Background";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { WeightBold } from "../../WeightBold/WeightBold";
import { CRMParagraph } from "../../Simple/CRMParagraph/CRMParagraph";
import { CRMButtonPrimary } from "../../CRMButtonPrimary/CRMButtonPrimary";
import { CRMButtonQuaternary } from "../../CRMButtonQuaternary/CRMButtonQuaternary";
import CRMTextAreaComponent from "../../CRMTextAreaComponent/CRMTextAreaComponent";
import { TCaseComplianceRaiseForm, TCaseComplianceRaiseRevokeForm, TCaseComplianceRaisesForm } from "../../../../../domain/codecs/form/CaseComplianceForm";
import { TCaseComplianceRaiseType } from "../../../../../domain/codecs/CaseComplianceRaise";
import { THexColor } from "../../../models/StringLiterals";

type TCRMCaseComplianceRaisesPanelProps = {
    form: TCaseComplianceRaisesForm;
    onChange: (form: TCaseComplianceRaisesForm) => void;
    onUpdateNote: (form: TCaseComplianceRaiseForm) => void;
    onRevoke: (form: TCaseComplianceRaiseRevokeForm) => void;
};

const getCardTitle = (type: TCaseComplianceRaiseType): string =>
    type === "sar" ? "Raised as SAR" : "Raised with insurers";

const getCardColors = (type: TCaseComplianceRaiseType): { background: THexColor; border: THexColor } =>
    type === "sar"
        ? { background: CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_12, border: CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_10 }
        : { background: CRMColors.HIGHLIGHTS_EASY_ORANGE_12, border: CRMColors.HIGHLIGHTS_EASY_ORANGE_10 };

export const CRMCaseComplianceRaisesPanel = (props: React.PropsWithChildren<TCRMCaseComplianceRaisesPanelProps>): JSX.Element => {
    const [editingNoteFor, setEditingNoteFor] = useState<string | null>(null);
    const [confirmingRevokeFor, setConfirmingRevokeFor] = useState<string | null>(null);

    const onChangeNote = (raiseId: string, note: string): void => {
        props.onChange({
            ...props.form,
            children: {
                ...props.form.children,
                raises: props.form.children.raises.map((r) =>
                    r.children.id === raiseId
                        ? { ...r, edited: { ...r.edited, note: note === "" ? null : note } }
                        : r
                ),
            },
        });
    };

    const renderCard = (raise: TCaseComplianceRaiseForm): JSX.Element => {
        const colors = getCardColors(raise.children.type);
        const isEditingNote = editingNoteFor === raise.children.id;
        const isConfirmingRevoke = confirmingRevokeFor === raise.children.id;

        return (
            <Background
                key={raise.children.id}
                color={colors.background}
                borderDefinition={`1px solid ${colors.border}`}
                borderRadius="5px"
                padding={CRMSpacing.MEDIUM}
            >
                <SpacingColumn spacing={CRMSpacing.SMALL}>
                    <SpacingRow spacing={CRMSpacing.SMALL} justifyContent="space-between">
                        <WeightBold>{getCardTitle(raise.children.type)}</WeightBold>

                        {!isConfirmingRevoke &&
                            <CRMButtonQuaternary
                                label="Un-mark"
                                onClick={() => setConfirmingRevokeFor(raise.children.id)}
                            />
                        }
                    </SpacingRow>

                    <CRMParagraph>
                        Raised on <WeightBold>{DateTime.fromISO(raise.children.raised_at).toFormat("d LLL yyyy")}</WeightBold> by <WeightBold>{raise.children.raised_by_name}</WeightBold>.
                    </CRMParagraph>

                    {isEditingNote
                        ? (
                            <SpacingColumn spacing={CRMSpacing.TINY}>
                                <CRMTextAreaComponent
                                    value={raise.edited.note ?? ""}
                                    placeholder="Reference (e.g. NCA SAR reference / insurer claim ref)"
                                    onChange={(value) => onChangeNote(raise.children.id, value)}
                                />
                                <SpacingRow spacing={CRMSpacing.TINY}>
                                    <CRMButtonQuaternary
                                        label="Save reference"
                                        onClick={() => {
                                            props.onUpdateNote(raise);
                                            setEditingNoteFor(null);
                                        }}
                                    />
                                    <CRMButtonQuaternary
                                        label="Cancel"
                                        onClick={() => setEditingNoteFor(null)}
                                    />
                                </SpacingRow>
                            </SpacingColumn>
                        )
                        : (
                            <SpacingRow spacing={CRMSpacing.SMALL}>
                                {raise.edited.note
                                    ? <CRMParagraph>Reference: <WeightBold>{raise.edited.note}</WeightBold></CRMParagraph>
                                    : <CRMParagraph>No reference recorded yet.</CRMParagraph>
                                }
                                <CRMButtonQuaternary
                                    label={raise.edited.note ? "Edit reference" : "Add reference"}
                                    onClick={() => setEditingNoteFor(raise.children.id)}
                                />
                            </SpacingRow>
                        )
                    }

                    {isConfirmingRevoke &&
                        <SpacingColumn spacing={CRMSpacing.TINY}>
                            <CRMParagraph>Take this mark off the case? It stays in the audit trail.</CRMParagraph>
                            <SpacingRow spacing={CRMSpacing.TINY}>
                                <CRMButtonPrimary
                                    label="Confirm un-mark"
                                    highlight="instructional-brick"
                                    onClick={() => {
                                        setConfirmingRevokeFor(null);
                                        props.onRevoke(raise.children.revoke_form);
                                    }}
                                />
                                <CRMButtonQuaternary
                                    label="Cancel"
                                    onClick={() => setConfirmingRevokeFor(null)}
                                />
                            </SpacingRow>
                        </SpacingColumn>
                    }
                </SpacingColumn>
            </Background>
        );
    };

    if (props.form.children.raises.length === 0) {
        return <></>;
    }

    return (
        <Background padding={CRMSpacing.MEDIUM}>
            <SpacingColumn spacing={CRMSpacing.SMALL}>
                {props.form.children.raises.map(renderCard)}
            </SpacingColumn>
        </Background>
    );
};
